import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';

import Card from '@/components/Card/Card';
import TextComponent from '@/components/TextComponent';
import { checkInput } from '@/utils/form';

import BaseInfo from './Containers/BaseInfo';
import PharmacyInfo from './Containers/PharmacyInfo';
import Business from './Containers/BusinessInfo';
import ButtonComponent from '@/components/ButtonComponent';
import GlobalAlert from '@/components/GlobalAlert';
import useAuthQuery from '@/hooks/auth/useAuthQuery';
import { SignupForm } from '@/apis/auth/interface';
import { useBanksQuery } from '@/hooks/common/useBanksQuery';
import theme from '@/styles/theme';

export type AcceptedFiles = [
  'application/pdf',
  'image/jpg',
  'image/jpeg',
  'image/png'
];

const SignupPage = () => {
  const [phoneAuth, setPhoneAuth] = useState<boolean>(false);
  const { signupMutation } = useAuthQuery();
  const { data: banks } = useBanksQuery();

  const { control, setValue, getValues, watch, handleSubmit, errors } =
    useForm<SignupForm>({
      defaultValues: {
        roadAddress: '',
        jibunAddress: '',
        licenseFileName: '',
        bankbookFileName: '',
        corporateRegistrationFileName: '',
        longitude: '',
        zonecode: ''
      },
      shouldUnregister: false
    });

  const setAddressForm = (data: AddressForm) => {
    setValue('roadAddress', data.roadAddress);
    setValue('jibunAddress', data.jibunAddress);
    setValue('longitude', data.longitude);
    setValue('latitude', data.latitude);
    setValue('zonecode', data.zonecode);
  };

  const { jibunAddress, roadAddress } = getValues();
  const [postCodeModal, setPostCodeModal] = useState<boolean>(false);
  const [license, setLicense] = useState<any>();
  const [bankbookImage, setBankbookImage] = useState<any>();
  const [corporateRegistrationImage, setCorporateRegistrationImage] =
    useState<any>();
  const [fileName, setFileName] = useState<any>({
    license: '',
    bankbook: '',
    corporateRegistration: ''
  });

  const signupStep = [
    { id: 0, step: 'baseInfo', title: '기본 정보' },
    { id: 1, step: 'pharmacyInfo', title: '약국 정보' },
    { id: 2, step: 'businessInfo', title: '사업자 등록 정보' }
  ];

  const [currentStep, setCurrentStep] = useState(signupStep[0]);

  const addImageFile = (file: Array<File>, type: string) => {
    const newFileName = { ...fileName };
    newFileName[type] = file[0].name;
    setFileName(newFileName);
    switch (type) {
      case 'license':
        setLicense(file[0]);
        return setValue('licenseFileName', file[0].name);
      case 'bankbook':
        setBankbookImage(file[0]);
        return setValue('bankbookFileName', file[0].name);
      case 'corporateRegistration':
        setCorporateRegistrationImage(file[0]);
        return setValue('corporateRegistrationFileName', file[0].name);
    }
  };

  const selectBank = (bank: string) => {
    setValue('bank', bank);
  };
  const inputDone = checkInput(watch());

  const onChangeStep = (direction: string) => {
    const prev = direction === 'prev';
    const currentStepIdx = currentStep.id;

    if (prev) {
      setCurrentStep(signupStep[currentStepIdx - 1]);
    } else {
      setCurrentStep(signupStep[currentStepIdx + 1]);
    }
    window.scrollTo(0, 0);
  };

  const onSubmit = (signupForm: SignupForm) => {
    if (!(inputDone && phoneAuth && license))
      return GlobalAlert.show({
        title: '입력정보를 확인해주세요.'
      });
    if (signupForm.password !== signupForm.passwordCheck) {
      return GlobalAlert.show({
        title: '비밀번호가 일치하지 않습니다.'
      });
    }
    signupForm.phone = signupForm.phone.trim();

    signupMutation.mutate({
      ...signupForm,
      license,
      bankbookImage,
      corporateRegistrationImage
    });
  };

  useEffect(() => {
    if (banks) {
      setValue('bank', banks[0]);
    }
  }, [banks]);

  return (
    <Wrapper>
      <SignupWrapper>
        <Card center>
          <CardBody>
            <StepWrapper>
              {signupStep.map((i, key) => (
                <Step active={currentStep.id === i.id} key={key}>
                  <StepCircle active={currentStep.id === i.id}>
                    <CircleInNumber>{i.id + 1}</CircleInNumber>
                  </StepCircle>
                  {currentStep.id === i.id && <StepText>{i.title}</StepText>}
                </Step>
              ))}
            </StepWrapper>
            <BaseInfo
              display={currentStep.id === 0}
              control={control}
              errors={errors}
              phoneAuth={phoneAuth}
              setPhoneAuth={setPhoneAuth}
              setValue={setValue}
            />
            <PharmacyInfo
              display={currentStep.id === 1}
              control={control}
              errors={errors}
              setAddressForm={setAddressForm}
              jibunAddress={jibunAddress || ''}
              roadAddress={roadAddress || ''}
              postCodeModal={postCodeModal}
              setPostCodeModal={setPostCodeModal}
              addImageFile={addImageFile}
              fileName={fileName}
            />
            <Business
              display={currentStep.id === 2}
              control={control}
              errors={errors}
              addImageFile={addImageFile}
              fileName={fileName}
              banks={banks}
              selectBank={selectBank}
            />
            <ButtonWrapper right={currentStep.id === 0}>
              <Error>
                {!!errors && currentStep.id === 2 && (
                  <ErrorText>
                    {
                      (
                        errors[
                          Object.keys(errors)?.[0] as keyof SignupForm
                        ] as any
                      )?.message
                    }
                  </ErrorText>
                )}
              </Error>
              {currentStep.id !== 0 && (
                <ButtonComponent
                  bgColor={theme.DF_COLOR.GREEN500}
                  borderColor={theme.DF_COLOR.GREEN500}
                  textColor={theme.DF_COLOR.WHITE}
                  fontWeight={600}
                  fontSize={20}
                  text={'이전 단계로'}
                  onClick={() => onChangeStep('prev')}
                />
              )}
              {currentStep.id + 1 !== signupStep.length ? (
                <ButtonComponent
                  bgColor={theme.DF_COLOR.GREEN500}
                  borderColor={theme.DF_COLOR.GREEN500}
                  textColor={theme.DF_COLOR.WHITE}
                  fontWeight={600}
                  fontSize={20}
                  text={'다음 단계로'}
                  onClick={() => onChangeStep('next')}
                />
              ) : (
                <ButtonComponent
                  bgColor={theme.DF_COLOR.GREEN500}
                  borderColor={theme.DF_COLOR.GREEN500}
                  textColor={theme.DF_COLOR.WHITE}
                  fontWeight={600}
                  fontSize={20}
                  text={'회원가입'}
                  onClick={handleSubmit(onSubmit)}
                />
              )}
            </ButtonWrapper>
          </CardBody>
        </Card>
      </SignupWrapper>
    </Wrapper>
  );
};

export default SignupPage;

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const SignupWrapper = styled.div`
  width: 1300px;
`;

const CardBody = styled.div`
  padding: 4rem 0;
  width: 65%;
`;

const ButtonWrapper = styled.div<{ right: boolean }>`
  margin: 5rem 0;
  display: flex;
  justify-content: ${({ right }) => (right ? 'flex-end' : 'space-between')};
  width: 100%;
  position: relative;
`;

const StepWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-bottom: 50px;

  > * {
    &:last-child {
      margin: 0;
    }
  }
`;
const Step = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  margin-right: ${({ active }) => (active ? 20 : 10)}px;
`;

const StepCircle = styled.div<{ active: boolean }>`
  background: ${theme.DF_COLOR.GREEN500};
  width: 28px;
  height: 28px;
  border-radius: 14px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ active }) => !active && 'opacity: 0.3'};
`;

const CircleInNumber = styled(TextComponent)`
  font-size: 20px;
  font-weight: 800;
  color: white;
`;

const StepText = styled(TextComponent)`
  font-size: 20px;
  font-weight: 600;
  color: ${theme.DF_COLOR.GREEN500};
  margin-left: 5px;
`;

const Error = styled.div`
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: center;
  top: -3rem;
`;

const ErrorText = styled(TextComponent)`
  color: ${theme.DF_COLOR.PRIMARY500};
`;
